/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Container,
  Stack,
  useMediaQuery
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { patchApi } from 'views/services/api';

import StepWelcome from './steps/StepWelcome';
import StepRooms from './steps/StepRooms';
import StepStayTypes from './steps/StepStayTypes';
import StepRates from './steps/StepRates';
import StepHotelProfile from './steps/StepHotelProfile';
import StepSegoIA from './steps/StepSegoIA';
import StepDone from './steps/StepDone';

// Los índices deben coincidir con onboardingStep en la base de datos
// (y con STEP_LABELS del embudo en el dashboard admin).
export const STEPS = ['Bienvenida', 'Habitaciones', 'Estancias', 'Precios', 'Tu hotel', 'Sego IA', '¡Listo!'];

export default function OnboardingWizard({ open, hotel, onPostpone, onFinished }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const hotelId = hotel?.hotelId || hotel?._id;

  const initialStep = Math.min(Math.max(Number(hotel?.onboardingStep) || 0, 0), STEPS.length - 1);
  const [activeStep, setActiveStep] = useState(initialStep);

  const persistStep = async (step) => {
    try {
      await patchApi(`api/hotel/onboarding/${hotelId}`, { step });
    } catch (e) {
      // No bloqueamos el avance si falla el guardado del progreso
      console.error('Error guardando progreso de onboarding:', e);
    }
  };

  const goNext = () => {
    const next = Math.min(activeStep + 1, STEPS.length - 1);
    setActiveStep(next);
    persistStep(next);
  };

  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleFinish = async () => {
    try {
      await patchApi(`api/hotel/onboarding/${hotelId}`, { completed: true });
    } catch (e) {
      console.error('Error marcando onboarding como completado:', e);
    }
    onFinished();
  };

  const stepProps = { hotel, hotelId, onNext: goNext, onBack: goBack };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <StepWelcome {...stepProps} />;
      case 1:
        return <StepRooms {...stepProps} />;
      case 2:
        return <StepStayTypes {...stepProps} />;
      case 3:
        return <StepRates {...stepProps} />;
      case 4:
        return <StepHotelProfile {...stepProps} />;
      case 5:
        return <StepSegoIA {...stepProps} />;
      case 6:
        return <StepDone {...stepProps} onFinish={handleFinish} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} fullScreen={fullScreen} fullWidth maxWidth="md" scroll="body">
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: '#fff'
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
              Configura tu hotel
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {hotel?.name || ''} · Paso {activeStep + 1} de {STEPS.length}
            </Typography>
          </Box>
          {activeStep < STEPS.length - 1 && (
            <Button
              size="small"
              startIcon={<ScheduleIcon />}
              onClick={() => onPostpone(activeStep)}
              sx={{ color: '#fff', borderColor: alpha('#fff', 0.5), textTransform: 'none' }}
              variant="outlined"
            >
              Configurar más tarde
            </Button>
          )}
        </Stack>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 2, pt: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Contenido del paso */}
      <Container maxWidth="md" sx={{ py: 3, minHeight: 380 }}>
        {renderStep()}
      </Container>
    </Dialog>
  );
}
